import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Centralized image gallery configuration
const galleryImages = [
    require('../images/gallery/garage-1.jpg'),
    require('../images/gallery/garage-2.jpg'),
    require('../images/gallery/garage-3.jpg'),
    require('../images/gallery/insulation-greenstuf.png'),
    require('../images/gallery/insulation-knauf.jpg'),
    require('../images/gallery/insulation-mammoth.png'),
    require('../images/gallery/insulation-pinkbatts.jpg'),
];

function About() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Force scroll to top and ensure it's applied
        window.scrollTo(0, 0);

        // Add a small delay to ensure the scroll is applied
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Calculate previous and next image indices
    const prevImageIndex = currentImageIndex === 0 ? galleryImages.length - 1 : currentImageIndex - 1;
    const nextImageIndex = currentImageIndex === galleryImages.length - 1 ? 0 : currentImageIndex + 1;

    // Get display images
    const displayImages = {
        prev: galleryImages[prevImageIndex],
        current: galleryImages[currentImageIndex],
        next: galleryImages[nextImageIndex],
    };

    const nextImage = () => {
        setCurrentImageIndex(nextImageIndex);
    };

    const prevImage = () => {
        setCurrentImageIndex(prevImageIndex);
    };

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-cream" style={{ opacity: 1 }}>
            {/* Hero Section */}
            <section className="bg-brand-green text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h1
                        className="text-4xl md:text-6xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        About Mega Property Solutions
                    </motion.h1>
                    <motion.p
                        className="text-xl md:text-2xl max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        A locally owned and operated insulation company specializing in complete insulation services for residential, commercial, and industrial projects.
                    </motion.p>
                </div>
            </section>

            {/* Company Story */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <motion.h2
                                className="text-3xl font-bold text-black mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Our Story
                            </motion.h2>
                            <motion.p
                                className="text-lg text-black mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                With years of experience in the industry, our team understands what it takes to deliver warm, energy-efficient, and code-compliant spaces. Whether it's thermal insulation, acoustic control, or fire-rated safety systems, we approach every job with professionalism, attention to detail, and a commitment to quality.
                            </motion.p>
                            <motion.p
                                className="text-lg text-black mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                We're proud to work with some of New Zealand's most trusted building supply brands — including Placemakers, Mitre10, Bunnings, and ITM — giving us access to the best insulation materials on the market at highly competitive rates. This means faster lead times, better value for our clients, and reliable results that stand the test of time.
                            </motion.p>
                            <motion.p
                                className="text-lg text-black"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                            >
                                At Mega Property Solutions, we believe in doing things right — with clear communication, clean and safe worksites, and exceptional workmanship from start to finish.
                            </motion.p>
                        </motion.div>
                        <motion.div
                            className="relative h-96 rounded-2xl overflow-hidden"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <img
                                src={require('../images/pro-builders.jpg')}
                                alt="Mega Property Solutions professional team"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Image Gallery/Carousel Section */}
            <section className="py-16 bg-brand-green w-full">
                <div className="w-full px-4">
                    <div className="max-w-6xl mx-auto mb-12">
                        <motion.h2
                            className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            Experience property excellence with Mega Property Solutions in Auckland
                        </motion.h2>
                        <motion.p
                            className="text-lg text-brand-cream text-center mb-12 max-w-4xl mx-auto"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        >
                            Our skilled team delivers top-notch maintenance and improvement
                            services for all property types. Committed to quality
                            and precision, we ensure your property performs at its best. Trust
                            confidently with Mega Property Solutions, where expertise meets reliability
                            in every service.
                        </motion.p>
                    </div>

                    {/* 3-Image Carousel - Full Width */}
                    <div className="relative w-full">
                        <div className="flex items-center justify-center w-full gap-4">
                            {/* Left Image (Half visible) */}
                            <motion.div
                                className="w-1/4 h-64 md:h-[68vh] overflow-hidden rounded-2xl"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                                style={{ filter: 'blur(3px)' }}
                            >
                                <img
                                    src={displayImages.prev}
                                    alt="Previous project"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "flex";
                                    }}
                                />
                                <div
                                    className="w-full h-full bg-gray-700 flex items-center justify-center"
                                    style={{ display: "none" }}
                                >
                                    <p className="text-gray-400 text-center text-sm">
                                        [Previous Image]
                                    </p>
                                </div>
                            </motion.div>

                            {/* Center Image (Full visible) */}
                            <motion.div
                                className="w-1/2 h-64 md:h-[68vh] overflow-hidden rounded-2xl"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                            >
                                <img
                                    src={displayImages.current}
                                    alt="Current project"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "flex";
                                    }}
                                />
                                <div
                                    className="w-full h-full bg-gray-700 flex items-center justify-center"
                                    style={{ display: "none" }}
                                >
                                    <p className="text-gray-400 text-center">
                                        [Main Image - Replace with actual image URL]
                                    </p>
                                </div>
                            </motion.div>

                            {/* Right Image (Half visible) */}
                            <motion.div
                                className="w-1/4 h-64 md:h-[68vh] overflow-hidden rounded-2xl"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                                style={{ filter: 'blur(3px)' }}
                            >
                                <img
                                    src={displayImages.next}
                                    alt="Next project"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "flex";
                                    }}
                                />
                                <div
                                    className="w-full h-full bg-gray-700 flex items-center justify-center"
                                    style={{ display: "none" }}
                                >
                                    <p className="text-gray-400 text-center text-sm">
                                        [Next Image]
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Navigation Arrows */}
                        <motion.button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 rounded-full p-3 transition-all duration-200 shadow-lg z-10"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </motion.button>

                        <motion.button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 rounded-full p-3 transition-all duration-200 shadow-lg z-10"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </motion.button>

                        {/* Pagination Dots */}
                        <div className="flex justify-center mt-6 space-x-2">
                            {galleryImages.map((_, index) => (
                                <motion.button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${currentImageIndex === index
                                        ? 'bg-brand-orange'
                                        : 'bg-white bg-opacity-60 hover:bg-opacity-80'
                                        }`}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                />
                            ))}
                        </div>

                    </div>
                </div>
            </section>

            {/* Our Values Section */}
            <section className="py-24 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <motion.h2
                            className="text-3xl font-bold text-black mb-4"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            Our Values
                        </motion.h2>
                        <motion.p
                            className="text-xl text-black"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            The principles that guide everything we do.
                        </motion.p>
                    </motion.div>

                    {/* Values Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            { title: "Quality Materials & Expert Installation", description: "We use only the best materials and ensure expert installation every time." },
                            { title: "Honest, Transparent Pricing", description: "No hidden costs or surprises - clear, upfront pricing for all our services." },
                            { title: "On-Time, Hassle-Free Service", description: "We respect your time and deliver projects on schedule with minimal disruption." },
                            { title: "Fully Compliant with NZ Building Code", description: "All our work meets or exceeds New Zealand building standards and regulations." },
                            { title: "Dedicated to Customer Satisfaction", description: "Your satisfaction is our priority - we stand behind our work with comprehensive guarantees." },
                            { title: "Strong Supplier Partnerships", description: "Our relationships with Placemakers, Mitre10, Bunnings, and ITM ensure quality materials at competitive prices." }
                        ].map((value, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-6 rounded-lg shadow-md"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ scale: 1.05, y: -5 }}
                            >
                                <div className="flex items-center mb-4">
                                    <div className="w-2 h-2 bg-brand-orange rounded-full mr-3"></div>
                                    <h3 className="text-lg font-semibold text-black">{value.title}</h3>
                                </div>
                                <p className="text-gray-600">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default About;
